'use client';

import { CandidateNavigationProvider, useCandidateNavigation } from '@components/contexts/CandidateNavigationContext';
import { BestieChatStateProvider, useBestieChatState } from '@components/contexts/BestieChatStateContext';
import { FilterDrawerProvider } from '@components/contexts/FilterDrawerContext';
import { JobListContent } from '@components/features/job-list-content';
import { CandidatePromptBar, ChatHistoryButton } from '@components/features/candidate-prompt-bar';
import { GuestLegalBar } from '@components/layout/GuestLegalBar';

interface BestieChatViewProps {
  locale: Locale;
  isAuthenticated: boolean;
}

/**
 * @description Standalone Bestie chat experience — single column, no sidebar.
 *
 * Mounts only the providers the candidate chat needs (search navigation, shared chat stream,
 * filter drawer) and renders the chat content (`JobListContent` in the `new-search` view) with
 * the prompt bar pinned at the bottom in chat mode. No `TwoColumnLayout`, no jobs sidebar.
 */
const BestieChatInner = ({ locale, isAuthenticated }: BestieChatViewProps) => {
  const { navigateToCandidateSearch } = useCandidateNavigation();
  const { submitChat, isGreetingPending } = useBestieChatState();

  return (
    <div className="flex min-h-screen flex-col bg-surface-100 pb-56 lg:pb-52">
      <JobListContent locale={locale} isAuthenticated={isAuthenticated} />
      {/* Legal links stay SSR'd here — they live in the page flow rather than inside the fixed
          prompt bar so crawlers see them. `/` points at the job list again, which carries its
          own bar, so this one is a leftover from when the chat was the homepage; it is kept
          because it costs nothing and the homepage may move back. */}
      {!isAuthenticated && (
        <div className="container mx-auto px-4">
          <GuestLegalBar />
        </div>
      )}
      <CandidatePromptBar
        locale={locale}
        isAuthenticated={isAuthenticated}
        onSearchSubmit={navigateToCandidateSearch}
        onChatSubmit={submitChat}
        isGreetingPending={isGreetingPending}
        chatRightSlot={<ChatHistoryButton />}
      />
    </div>
  );
};

export const BestieChatView = ({ locale, isAuthenticated }: BestieChatViewProps) => (
  <CandidateNavigationProvider locale={locale} initialView="new-search">
    <FilterDrawerProvider>
      {/* `isAuthenticated` is not passed here: the provider derives its own auth branch from the
          thread id (`anonSessionId`), which is reactive to the client session and can flip (e.g.
          in-place sign-in via AuthModal) while this SSR-computed prop stays stale for the rest of
          the mount. It is still passed below for SSR-only concerns (GuestLegalBar, prompt bar). */}
      <BestieChatStateProvider locale={locale}>
        <BestieChatInner locale={locale} isAuthenticated={isAuthenticated} />
      </BestieChatStateProvider>
    </FilterDrawerProvider>
  </CandidateNavigationProvider>
);
